import React, { useEffect, useState } from 'react'
import Calendar from 'react-calendar'
import { format } from 'date-fns'
import { AppCalendarWrapper } from 'components/Common/AppFormFields/AppPicker/AppPicker.style'
import moment from 'moment-timezone'
import { AppChevronDownIcon } from 'components/Common/AppSvgIcons/AppChevronDownIcon'

interface IPickerProps {
  type: 'date' | 'range' | 'year' | 'multiple'
  value: string | { start: string; end: string } | string[]
  onChange: (value: string | { start: string; end: string } | string[]) => void
  isdisable?: boolean
  minDate?: string
}

export const AppPicker: React.FC<IPickerProps> = ({
  type,
  onChange,
  value: propValue,
  isdisable,
  minDate,
}) => {
  const [value, setValue] = useState<Date | [Date, Date] | Date[] | null>()
  const [selectedDates, setSelectedDates] = useState<Date[]>([])
  const [view, setView] = useState<'month' | 'year' | 'decade'>(
    type === 'year' ? 'decade' : 'month'
  )

  // Synchronize state with propValue
  useEffect(() => {
    if (type === 'date' && typeof propValue === 'string') {
      const localDate = moment(propValue).local().toDate()
      setValue(localDate)
    } else if (
      type === 'range' &&
      typeof propValue === 'object' &&
      'start' in propValue &&
      'end' in propValue
    ) {
      const start = moment(propValue.start).local().toDate()
      const end = moment(propValue.end).local().toDate()
      setValue([start, end])
    } else if (type === 'year' && typeof propValue === 'string') {
      const yearDate = moment(`${propValue}-01-01`).local().toDate()
      setValue(yearDate)
    } else if (type === 'multiple' && Array.isArray(propValue)) {
      const dates = propValue.map((date) => moment(date).local().toDate())
      setSelectedDates(dates)
      setValue(null) // For multiple selection, we don't use the value prop
    } else {
      setValue(null)
    }
  }, [propValue, type])

  const handleChange = (newValue) => {
    if (type === 'multiple') {
      // For multiple selection, we handle the selection manually
      const clickedDate = newValue as Date
      const dateExists = selectedDates.some(
        (date) => moment(date).format('YYYY-MM-DD') === moment(clickedDate).format('YYYY-MM-DD')
      )

      let updatedDates: Date[]
      if (dateExists) {
        // Remove the date if it already exists
        updatedDates = selectedDates.filter(
          (date) => moment(date).format('YYYY-MM-DD') !== moment(clickedDate).format('YYYY-MM-DD')
        )
      } else {
        // Add the date if it doesn't exist
        updatedDates = [...selectedDates, clickedDate]
      }

      setSelectedDates(updatedDates)

      // Format dates and call onChange
      const formattedDates = updatedDates.map((date) => moment(date).local().format('MM/DD/YYYY'))
      onChange(formattedDates)
    } else {
      // Handle other types as before
      setValue(newValue)

      if (type === 'date' && newValue instanceof Date) {
        // Format date using moment in local timezone
        const localDate = moment(newValue).local().format('MM/DD/YYYY')
        onChange(localDate)
      }

      if (type === 'range' && Array.isArray(newValue)) {
        const [start, end] = newValue
        if (start && end) {
          onChange({
            start: moment(start).local().format('MM/DD/YYYY'),
            end: moment(end).local().format('MM/DD/YYYY'),
          })
        }
      }
    }
  }

  const handleYearClick = (date: Date) => {
    if (type === 'year') {
      const year = date.getFullYear().toString()
      onChange(year)
      setValue(date)
    }
  }

  const handleMonthClick = () => {
    setView('month')
  }

  // Custom tile class for multiple selection
  const tileClassName = ({ date }) => {
    if (type === 'multiple') {
      const isSelected = selectedDates.some(
        (selectedDate) =>
          moment(selectedDate).format('YYYY-MM-DD') === moment(date).format('YYYY-MM-DD')
      )
      return isSelected ? 'react-calendar__tile--active' : ''
    }
    return ''
  }
  return (
    <AppCalendarWrapper style={isdisable ? { pointerEvents: 'none', opacity: 0.5 } : {}}>
      <Calendar
        onChange={handleChange}
        value={value as Date | [Date, Date] | null}
        selectRange={type === 'range'}
        view={view}
        onClickYear={handleYearClick}
        onClickMonth={handleMonthClick}
        tileClassName={tileClassName}
        navigationLabel={({ date }) => {
          // Disable navigation click if type is 'year'
          return view === 'month' ? (
            <span
              className="flex flex-row justify-center gap-1 items-center"
              onClick={() => type !== 'year' && setView('year')}
            >
              {format(date, 'MMMM yyyy')}
              <AppChevronDownIcon color="black" />
            </span>
          ) : (
            <span className="disabled">{date.getFullYear()}</span>
          )
        }}
        tileDisabled={({ view }) => type === 'year' && view !== 'decade'}
        minDate={minDate ? new Date(minDate) : undefined}
      />
    </AppCalendarWrapper>
  )
}
